/* eslint-disable @typescript-eslint/no-explicit-any */

import { Property, Patient } from "../../types";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ContextualSaveBar, Loading } from "@medentic/polaris-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import PropertiesTabContent from "./PropertiesTabContent";
import OverviewTabContent from "./OverviewTabContent";
import TasksTabContent from "./TasksTabContent";
import AppointmentsTabContent from "./AppointmentsTabContent";
import TreatmentsTabContent from "./TreatmentsTabContent";
import DocumentsTabContent from "./DocumentsTabContent";
import ImagesTabContent from "./ImagesTabContent";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";

interface TabsContentProps {
  selectedTab: number;
  patient?: Patient;
  patientProperties: Property[];
  isLoadingPatient: boolean;
}

function TabsContent({
  selectedTab,
  patient,
  patientProperties,
  isLoadingPatient,
}: TabsContentProps) {
  const { t } = useTranslation();
  const { PATCH, POST, DELETE } = useApiRequest();
  const queryClient = useQueryClient();

  const { clinicId, patientId } = useParams<{
    patientId: string;
    clinicId: string;
  }>();
  const [updatedProperties, setUpdatedProperties] = useState<Partial<Patient>>(
    {}
  );
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Patient>) => {
      return PATCH(
        `/api/healthcare-companies/${clinicId}/patients/${patientId}`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patientDetails", patientId],
      });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      setTimeout(() => {
        setUpdatedProperties({});
      }, 1500);
    },
  });

  const fileUploadMutation = useMutation({
    mutationFn: async ({
      propertyName,
      file,
    }: {
      propertyName: string;
      file: File;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("data", JSON.stringify({ id: propertyName }));

      return POST(
        `/api/healthcare-companies/${clinicId}/patients/${patientId}/upload`,
        formData
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patientDetails", patientId],
      });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });

  const fileDeleteMutation = useMutation({
    mutationFn: async (propertyName: string) => {
      return DELETE(
        `/api/healthcare-companies/${clinicId}/patients/${patientId}/upload`,
        false,
        { id: propertyName }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patientDetails", patientId],
      });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });

  const validateProperties = useCallback(
    (properties: Property[], updates: Partial<Patient>) => {
      const errors: Record<string, string> = {};

      Object.entries(updates).forEach(([key, value]) => {
        const property = properties.find((p) => p.name === key);
        if (property?.regexValueValidation) {
          const regex = new RegExp(property.regexValueValidation.slice(1, -1));
          const testResult = regex.test(String(value));

          if (!testResult) {
            errors[key] =
              property.regexValueValidationError ??
              t("common.common.invalidFormat");
          }
        }
      });

      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    },
    [t]
  );

  const handleSave = useCallback(async () => {
    if (!validateProperties(patientProperties, updatedProperties)) {
      return;
    }
    updateMutation.mutate(updatedProperties);
  }, [
    updatedProperties,
    updateMutation,
    patientProperties,
    validateProperties,
  ]);

  const handleDiscard = useCallback(() => {
    setUpdatedProperties({});
    setValidationErrors({});
  }, []);

  const handlePropertyChange = useCallback(
    (propertyName: string, value: any) => {
      setUpdatedProperties((prev) => ({
        ...prev,
        [propertyName]: value,
      }));
      // Clear validation error for this field when it changes
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[propertyName];
        return newErrors;
      });
    },
    []
  );

  const handleFileUpload = useCallback(
    (propertyName: string, file: File) => {
      fileUploadMutation.mutate({ propertyName, file });
    },
    [fileUploadMutation]
  );

  const handleFileRemove = useCallback(
    (propertyName: string) => {
      fileDeleteMutation.mutate(propertyName);
    },
    [fileDeleteMutation]
  );

  const hasChanges = useCallback(() => {
    if (!patient) return false;

    return Object.entries(updatedProperties).some(([key, value]) => {
      const originalValue = patient[key as keyof Patient];
      return JSON.stringify(originalValue) !== JSON.stringify(value);
    });
  }, [patient, updatedProperties]);

  const renderContent = () => {
    switch (selectedTab) {
      case 0:
        return (
          <OverviewTabContent patient={patient} loading={isLoadingPatient} />
        );
      case 1:
        return (
          <PropertiesTabContent
            properties={patientProperties}
            patient={patient}
            loading={isLoadingPatient}
            onPropertyChange={handlePropertyChange}
            disabled={updateMutation.isPending}
            updatedValues={updatedProperties}
            onFileUpload={handleFileUpload}
            onFileRemove={handleFileRemove}
            validationErrors={validationErrors}
          />
        );

      case 2:
        return (
          <DocumentsTabContent patient={patient} loading={isLoadingPatient} />
        );
      case 3:
        return (
          <ImagesTabContent patient={patient} loading={isLoadingPatient} />
        );
      case 4:
        return <TasksTabContent patient={patient} loading={isLoadingPatient} />;
      case 5:
        return (
          <AppointmentsTabContent
            patient={patient}
            loading={isLoadingPatient}
          />
        );
      case 6:
        return (
          <TreatmentsTabContent patient={patient} loading={isLoadingPatient} />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {(fileUploadMutation.isPending || fileDeleteMutation.isPending) && (
        <Loading />
      )}
      {hasChanges() && (
        <ContextualSaveBar
          backgroundColor="#2B0069"
          saveAction={{
            onAction: handleSave,
            loading: updateMutation.isPending,
            disabled: updateMutation.isPending,
          }}
          discardAction={{
            onAction: handleDiscard,
            loading: updateMutation.isPending,
            disabled: updateMutation.isPending,
          }}
        />
      )}
      {renderContent()}
    </>
  );
}

export default TabsContent;
